import Keycloak from 'keycloak-js';
import axios from 'axios';
import * as Sentry from '@sentry/browser';
import { useAuthStore } from '../../store/auth';
import { AVAILABLE_TENANTS } from '../../constants';
import { removeDeviceApi } from '../../api/devices';
import { actualizeAccountApi } from '../../api/account';
import { usePinStore } from '../../store/pin';
import config from '../config';
import { saveLog } from '../../helpers/logs';
import { isProduction } from '../../helpers/isProduction';

const isJetEmployee = () => localStorage.getItem('isJetEmployee') === 'true';
const needToActualize = localStorage.getItem('needToActualizeAccount') === 'true';

export const getTenant = (): string => {
    if (isJetEmployee()) {
        return 'internal';
    }

    if (isProduction()) {
        return Object.keys(AVAILABLE_TENANTS).find((key) => AVAILABLE_TENANTS[key] === window.location.origin) ?? 'default';
    }

    const tenantFromUrl = new URLSearchParams(window.location.search).get('tenant');

    if (tenantFromUrl && AVAILABLE_TENANTS[tenantFromUrl]) {
        localStorage.setItem('tenant', tenantFromUrl);
        return tenantFromUrl;
    }

    return localStorage.getItem('tenant') || 'default';
};

const createKeycloakInstance = () => {
    const tenant = getTenant();
    const keycloakUrl = config.keycloakUrl[tenant];
    const clientId = isJetEmployee() ? 'tkwy-employee' : 'live-orders';
    const [url, realm] = keycloakUrl.split('/realms/');

    return new Keycloak({
        url,
        realm,
        clientId
    });
};

let keycloak = createKeycloakInstance();

export const getAssignedRestaurant = async () => {
    if (isJetEmployee() && keycloak.tokenParsed) return keycloak.tokenParsed.rids[0];
    const info = await keycloak.loadUserInfo();
    return (info as { rids: string[] }).rids[0];
};

const saveSelectedResturantId = async () => {
    const currentSelectedId = localStorage.getItem('selectedRestaurantId');
    const availableId = await getAssignedRestaurant();

    if (!currentSelectedId || availableId !== currentSelectedId) {
        localStorage.setItem('selectedRestaurantId', availableId);
    }
};

/**
 * For Jet employees we need to pass restaurant_id & tenant to the login url
 * This is not possible with the keycloak-js library, so we need to redirect manually
 */
const loginAsJetEmployee = async () => {
    const isAuthenticated = await keycloak.init({ checkLoginIframe: false });

    if (isAuthenticated && keycloak.token) {
        await saveSelectedResturantId();
        saveLog('[Auth] JET employee logged in');
        useAuthStore.getState().actions.loginSuccess(keycloak.token);

        return;
    }

    const selectedRestaurantId = localStorage.getItem('selectedRestaurantId');
    const loginUrl = keycloak.createLoginUrl({ idpHint: 'okta' });

    window.location.href = `${loginUrl}&tenant=legacy-tkwy&restaurant_id=${selectedRestaurantId}`;
};

export const login = async () => {
    if (window.Cypress) {
        const token = localStorage.getItem('testToken');
        useAuthStore.getState().actions.loginSuccess(token || '');
        return;
    }

    if (isJetEmployee()) {
        return loginAsJetEmployee();
    }

    try {
        const isAuthenticated = await keycloak.init({ onLoad: 'check-sso', checkLoginIframe: false });

        if (isAuthenticated && keycloak.token && keycloak.tokenParsed) {
            await saveSelectedResturantId();

            if (needToActualize) {
                localStorage.removeItem('needToActualizeAccount');
                await actualizeAccountApi();
            }

            saveLog('[Auth] User logged in');

            useAuthStore.getState().actions.loginSuccess(keycloak.token);
        } else {
            localStorage.setItem('needToActualizeAccount', 'true');
            keycloak.login({ redirectUri: window.location.href });
        }
    } catch (error) {
        saveLog('[Auth] Login failed', { error });
    }
};

/**
 * Used for cross-login
 */
export const loginByCode = async (code: string) => {
    const response = await axios.post(
        `${config.keycloakUrl.internal}/protocol/openid-connect/token`,
        {
            client_id: 'tkwy-employee',
            grant_type: 'authorization_code',
            code,
            redirect_uri: window.location.origin + window.location.pathname
        },
        {
            headers: {
                'content-type': 'application/x-www-form-urlencoded'
            }
        }
    );

    localStorage.setItem('isJetEmployee', 'true');

    keycloak = createKeycloakInstance();

    const isAuthenticated = await keycloak.init({
        token: response.data.access_token,
        refreshToken: response.data.refresh_token,
        idToken: response.data.id_token,
        checkLoginIframe: false
    });

    if (isAuthenticated && keycloak.tokenParsed && keycloak.token) {
        const { rids } = keycloak.tokenParsed;

        localStorage.setItem('selectedRestaurantId', rids[0]);
        useAuthStore.getState().actions.loginSuccess(keycloak.token);
    } else {
        saveLog('[Auth] Login by code failed');

        throw new Error('Login failed');
    }
};

export const getAccessToken = async () => {
    // For smoke tests & Cypress
    const testToken = localStorage.getItem('testToken');
    if (testToken) return testToken;

    await refreshTokenIfExpired();

    return keycloak.token;
};

let refreshTokenPromise: Promise<any> | null = null;

export const refreshTokenIfExpired = async () => {
    if (refreshTokenPromise) {
        return refreshTokenPromise;
    }

    try {
        if (!keycloak.isTokenExpired(30)) {
            return;
        }
    } catch (error) {
        Sentry.addBreadcrumb({
            category: 'Auth',
            message: 'Token expired check failed',
            data: error
        });

        keycloak.clearToken();
        keycloak.login({ redirectUri: window.location.href });
    }

    refreshTokenPromise = (async function () {
        try {
            await keycloak.updateToken(30);
            refreshTokenPromise = null;

            Sentry.addBreadcrumb({
                category: 'Auth',
                message: 'Token refreshed successfully'
            });
        } catch (error) {
            Sentry.addBreadcrumb({
                category: 'Auth',
                message: 'Token refresh failed',
                data: error
            });

            keycloak.clearToken();
            keycloak.login({ redirectUri: window.location.href });
        }
    })();

    return refreshTokenPromise;
};

const disablePushNotifications = async () => {
    const fcmToken = localStorage.getItem('fcmToken');

    if (!fcmToken) {
        return;
    }

    try {
        await removeDeviceApi(fcmToken);
    } catch (error) {
        !isProduction() && console.error('Failed to remove device', error);
    } finally {
        localStorage.removeItem('fcmToken');
    }
};

export const logout = async (reason?: string) => {
    useAuthStore.getState().actions.logoutStarted();

    await disablePushNotifications();
    await saveLog('[Auth] User logged out', { reason });

    ['isJetEmployee', 'selectedRestaurantId'].forEach((key) => localStorage.removeItem(key));

    await usePinStore.getState().actions.setToken(null);

    keycloak.logout();
};
